import tw from 'twin.macro'
import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { isEqual, isEmpty } from 'lodash'
import { useForm, FormProvider } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'

import { useAuth } from 'modules/Auth/hooks/useAuth'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import {
  useListenCouponsQuery,
  useListenOneTimeProductsQuery,
  useListenProductCurrencyQuery,
  useListenItemPreviewImgQuery,
  useListenStripeResponseQuery,
  useSetStripeRequestMutation,
  useUpdateOneTimeProductMutation,
} from '../productInfoApi'

import { SELL } from 'modules/App/components/Header'
import SalesPageBuilder from 'modules/PageBuilder/pages/SalesPageBuilder'
import CheckoutPageBuilder from 'modules/PageBuilder/pages/CheckoutPageBuilder'
import { Skeleton } from 'common/components/Skeleton/Skeleton'
import { FormRefsControlProvider } from 'common/components/RefsControl/FormRefsControl/context'
import { CreateDialogBanner } from 'common/components/CreateDialogBanner/CreateDialogBanner'
import { Dialog, DialogContent } from 'common/components/Dialog/Dialog'
import ProductForm, { getDefaultProductValues } from '../components/ProductForm/ProductForm'
import { ReadyForSaleSection } from '../components/ReadyForSale/ReadyForSaleSection'
import ContentForm from '../components/ContentForm/ContentForm'
import { createUID } from 'common/utils/createUID'
import { useAlert } from 'common/components/Alert/hooks/useAlert'
import { getProductCurrency } from '../utils/utils'
import { getOneTimeProductSlugs } from '../utils/oneTimeProductUtils'
import { useListenPublishedCheckoutPageQuery } from 'modules/PageBuilder/pageBuilderApi'
import { schema as productFormSchema } from 'modules/ProductInfo/components/ProductForm/schema'
import { accessDurationOptions } from '../components/ProductForm/AccessDurationInput'
import { getDayAnchorUtcNoon } from '../utils/dateUtils'

const TABS = {
  PRODUCT: { type: 'PRODUCT', displayName: 'Product' },
  CONTENT: { type: 'CONTENT', displayName: 'Content' },
  SALES_PAGE: { type: 'SALES_PAGE', displayName: 'Sales Page' },
  CHECKOUT_PAGE: { type: 'CHECKOUT_PAGE', displayName: 'Checkout Page' },
}

export default function OneTimeProduct() {
  const navigate = useNavigate()
  const { productId } = useParams()
  const { userId } = useAuth()

  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''
  const { data: publishedCheckoutPage } = useListenPublishedCheckoutPageQuery({ coachOrgId, productId })
  const publishedCheckoutPageLoading = publishedCheckoutPage === undefined || publishedCheckoutPage?.isLoading

  const { data: oneTimeProductsData } = useListenOneTimeProductsQuery({ coachOrgId })
  const oneTimeProductsLoading = oneTimeProductsData === undefined || oneTimeProductsData?.isLoading
  const oneTimeProductData = oneTimeProductsData?.[productId]

  const oneTimeProductSlugs = getOneTimeProductSlugs({ oneTimeProductsData, productId })

  const { data: currency } = useListenProductCurrencyQuery({ coachOrgId })
  const currencyLoading = currency === undefined || currency?.isLoading

  const { data: couponsData } = useListenCouponsQuery({ coachOrgId })
  const couponsLoading = couponsData === undefined || couponsData?.isLoading
  const coupons = couponsData || {}

  const loading =
    !productId || oneTimeProductsLoading || currencyLoading || couponsLoading || publishedCheckoutPageLoading

  useEffect(() => {
    if (!loading && !oneTimeProductData) {
      navigate(SELL)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading])

  if (loading) {
    return (
      <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16 pb-48'>
        <div className='w-full max-w-[90rem] mx-auto mt-6 md:mt-12'>
          <div className='flex items-center justify-between my-8'>
            <Skeleton css={tw`w-1/3`} />
          </div>
          <div className='flex items-center justify-start bg-white rounded-lg justify-self-start p-3 border-2 border-gray-200'>
            {Object.values(TABS).map((tab, index, arr) => (
              <button
                key={tab.type}
                tabIndex={-1}
                disabled={true}
                css={[
                  tw`font-semibold text-gray-500 hover:text-black transition-all px-6 py-2 rounded-lg`,
                  TABS.PRODUCT.type === tab.type && tw`bg-gray-100 text-black`,
                ]}
              >
                <span className='relative'>{tab.displayName}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <PageContent
      productId={productId}
      oneTimeProductData={oneTimeProductData}
      currency={currency}
      oneTimeProductSlugs={oneTimeProductSlugs}
      coupons={coupons}
      coachOrgId={coachOrgId}
    />
  )
}

function PageContent({ productId, oneTimeProductData, currency, oneTimeProductSlugs, coupons, coachOrgId }) {
  const { createAlert } = useAlert()

  const [updateOneTimeProduct] = useUpdateOneTimeProductMutation()
  const [setStripeRequest] = useSetStripeRequestMutation()
  const [tabChangeSaveConfirm, setTabChangeSaveConfirm] = useState(null)

  const [selectedTab, setSelectedTab] = useState(TABS.PRODUCT.type)
  const [isUpdatingProductForm, setIsUpdatingProductForm] = useState(false)

  const { data: stripeResponse } = useListenStripeResponseQuery({ coachOrgId, responseId: productId })
  const stripeResponsePending = stripeResponse?.status === 'pending'
  const errorMessage = stripeResponse?.error

  // The product form falls back to the thumbnail of the item being sold when the product has
  // no thumbnail, so the same fallback has to be part of the form defaults. Without it the
  // "unsaved changes" check would fire on a form the user never edited.
  const { data: itemImgData } = useListenItemPreviewImgQuery({
    coachOrgId,
    itemId: oneTimeProductData?.item?.id,
    itemType: oneTimeProductData?.item?.type,
  })
  // This listener returns { isLoading: true } until the url arrives.
  const itemImg = typeof itemImgData === 'string' ? itemImgData : ''

  const defaultProductFormValues = React.useMemo(
    () => getDefaultProductValues(oneTimeProductData, itemImg),
    [oneTimeProductData, itemImg]
  )
  const productFormMethods = useForm({
    defaultValues: defaultProductFormValues,
    values: defaultProductFormValues,
    resetOptions: { keepDirtyValues: true },
    mode: 'all',
    resolver: yupResolver(productFormSchema),
    context: { oneTimeProductSlugs },
  })

  const onSubmitProductForm = async (data) => {
    if (!isEmpty(productFormMethods.formState.errors)) {
      return
    }

    setIsUpdatingProductForm(true)
    let formData = {
      ...data,
      id: productId,
      paymentType: 'one-time',
      promo: data?.promo,
      currency,
      item: oneTimeProductData?.item || null,
    }

    const accessType = formData.accessDuration

    if (accessType === accessDurationOptions.forever) {
      formData.accessEnd = null
      formData.accessUnit = null
      formData.accessValue = null
    } else if (accessType === accessDurationOptions.date) {
      formData.accessUnit = null
      formData.accessValue = null
      formData.accessEnd = data.accessEnd ? getDayAnchorUtcNoon(data.accessEnd) : null
    } else if (accessType === accessDurationOptions.period) {
      formData.accessEnd = null
    }

    // Used for UI purposes only, not needed in db
    delete formData.accessDuration

    if (oneTimeProductData?.stripeProductName !== formData?.stripeProductName) {
      const productReqId = createUID()
      await setStripeRequest({
        requestId: productReqId,
        coachOrgId,
        type: 'UPDATE_ONE_TIME_PRODUCT',
        payload: {
          trybeProductId: productId,
          stripeProductId: oneTimeProductData?.stripeProductId,
          name: data.stripeProductName,
        },
      })
    }

    if (oneTimeProductData?.price !== formData?.price) {
      const priceReqId = createUID()
      await setStripeRequest({
        requestId: priceReqId,
        coachOrgId,
        type: 'UPDATE_ONE_TIME_PRICE',
        payload: {
          stripeProductId: oneTimeProductData?.stripeProductId,
          unitAmount: formData.price,
          trybeProductId: productId,
          currency,
          stripePriceId: oneTimeProductData?.stripe_price_id,
        },
      })
    }

    await updateOneTimeProduct({ coachOrgId, product: formData, productId })
    createAlert({ text: 'Product saved!', type: 'success' })
    setIsUpdatingProductForm(false)
  }

  const handleConfirmSave = () => {
    if (!isEmpty(productFormMethods.formState.errors)) {
      createAlert({ text: `Please fix form errors`, type: 'danger' })
      setTabChangeSaveConfirm(null)
      return
    } else {
      onSubmitProductForm(productFormMethods.getValues())
      setSelectedTab(tabChangeSaveConfirm)
      setTabChangeSaveConfirm(null)
    }
  }

  const handleSelectTab = (tab) => {
    if (tab.type !== TABS.PRODUCT.type && selectedTab === TABS.PRODUCT.type) {
      const productFormState = productFormMethods.getValues()
      if (isEqual(productFormState, defaultProductFormValues)) {
        setSelectedTab(tab.type)
      } else {
        setTabChangeSaveConfirm(tab.type)
      }
    } else {
      setSelectedTab(tab.type)
    }
  }

  useEffect(() => {
    if (errorMessage) {
      createAlert({ text: `Publish failed. ${errorMessage}`, type: 'danger' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorMessage])

  return (
    <div className='flex flex-col min-h-screen bg-offWhite px-4 md:px-16 pb-48'>
      <div className='w-full max-w-[90rem] mx-auto mt-6 md:mt-12'>
        <h1 className='font-bold text-3xl md:text-5xl text-tBlack capitalize mb-6'>
          {oneTimeProductData?.stripeProductName || 'Untitled Product'}
        </h1>
        <div className='flex items-center justify-start bg-white rounded-lg justify-self-start p-3 border-2 border-gray-200'>
          {Object.values(TABS).map((tab, index, arr) => (
            <button
              key={tab.type}
              tabIndex={-1}
              onClick={() => handleSelectTab(tab)}
              css={[
                tw`font-semibold text-gray-500 hover:text-black transition-all px-6 py-2 rounded-lg`,
                selectedTab === tab.type && tw`bg-gray-100 text-black`,
              ]}
            >
              <span className='relative'>{tab.displayName}</span>
            </button>
          ))}
        </div>
        <ReadyForSaleSection coachOrgId={coachOrgId} productId={productId} product={oneTimeProductData} />
        {selectedTab === TABS.PRODUCT.type && (
          <FormProvider {...productFormMethods}>
            <FormRefsControlProvider>
              <ProductForm
                productId={productId}
                product={oneTimeProductData}
                coachOrgId={coachOrgId}
                currency={getProductCurrency({
                  productCurrency: oneTimeProductData?.currency,
                  defaultCurrency: currency,
                })}
                onSubmit={onSubmitProductForm}
                isUpdating={isUpdatingProductForm}
                stripeResponsePending={stripeResponsePending}
                coupons={coupons}
              />
            </FormRefsControlProvider>
          </FormProvider>
        )}
        {selectedTab === TABS.CONTENT.type && (
          <FormRefsControlProvider>
            <ContentForm coachOrgId={coachOrgId} item={oneTimeProductData?.item} productId={productId} />
          </FormRefsControlProvider>
        )}
        {selectedTab === TABS.SALES_PAGE.type && <SalesPageBuilder />}
        {selectedTab === TABS.CHECKOUT_PAGE.type && <CheckoutPageBuilder />}
        <Dialog open={Boolean(tabChangeSaveConfirm)} setOpen={setTabChangeSaveConfirm}>
          <DialogContent>
            <CreateDialogBanner
              titleText='Save changes?'
              text='You have unsaved changes in your form'
              handleCreate={handleConfirmSave}
              createBtnText='Save'
              cancelBtnText='Discard'
              handleCancel={() => {
                productFormMethods.reset(defaultProductFormValues)
                setSelectedTab(tabChangeSaveConfirm)
                setTabChangeSaveConfirm(null)
              }}
              loading={isUpdatingProductForm}
            />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
